import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface SegmentedFilterOption<T extends string> {
  value: T;
  label: string;
}

interface SegmentedFilterProps<T extends string> {
  options: SegmentedFilterOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Accessible label for the control. */
  ariaLabel?: string;
  /** Extra classes for the outer wrapper. */
  className?: string;
}

// Radix Select forbids an empty-string item value; map "" to a sentinel.
const EMPTY = "__all__";

/**
 * Filter control that renders as a full-width dropdown on mobile and the
 * calendar-style segmented tabs from `sm` up.
 */
export function SegmentedFilter<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
}: SegmentedFilterProps<T>) {
  const selected = options.find((o) => o.value === value);

  return (
    <div className={cn("w-full sm:w-auto", className)}>
      {/* Mobile: full-width dropdown */}
      <div className="sm:hidden">
        <Select
          value={value === ("" as T) ? EMPTY : value}
          onValueChange={(raw) => onChange((raw === EMPTY ? "" : raw) as T)}
        >
          <SelectTrigger aria-label={ariaLabel} className="h-10 w-full rounded-xl">
            <SelectValue>{selected?.label}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value === ("" as T) ? EMPTY : o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Desktop: segmented tabs */}
      <div
        role="tablist"
        aria-label={ariaLabel}
        className="hidden h-10 w-max items-center gap-1 rounded-xl border border-border/70 bg-card/60 p-1 shadow-sm sm:flex"
      >
        {options.map((o) => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(o.value)}
              className={cn(
                "h-full whitespace-nowrap rounded-lg px-4 text-sm font-medium transition-all duration-200",
                active
                  ? "bg-primary text-primary-foreground shadow-sm shadow-primary/25"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
